import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogHeader,
  DialogFooter,
  DialogBody,
} from '@axa-asia/emma-stylesheet/lib/components/Dialog';
import { goto } from '../../../../actions/historyActions';
import EmmaNavTitle from '../../../../components/EmmaNavTitle';
import { t } from '../../../../locales';
import {
  autoCompleteMapAddressChangedConfirm,
  autoCompleteMapAddressReset,
  loadGoogleAutoCompleteMap,
  setIsAutoFilled,
  setIsChosenNonHKAddress,
} from '../../actions/changeAddressAction';
import { getSelectedPolicy } from '../../selectors/changeContactSelector';
import './style.scss';
import browserHistory from '../../../../stores/browserHistory';
import {
  getAddressFromMapView,
  getAddressOutOfHKSystemMessage,
  getChangeAddressLabels,
  getIsGoogleMapJsScriptComplete,
} from '../../selectors/changeAddressSelector';
import { isApp, resolveLocalizedText } from '../../../../utils';
import {
  EmmaButton,
  EmmaLoadingSpinner,
  EmmaSpace,
  EmmaSystemMessage,
  EmmaText,
} from '@axa-asia/emma-stylesheet';
import classNames from 'classnames';
import { MapAddressInputType } from '../../reducers/changeAddressReducer';
import { LocalizedText } from 'src/types/LocalizedTextTypes';
import { EmmaFactory } from '@axa-asia/hk-emma-js';
import ChangeAddressContent from '../../i18n/changeAddressContent';
import {
  triggerPopupCloseScreenChange,
  triggerPopupDisplayScreenChange,
} from '@axa-asia/hk-emma-web-module/lib/ga-lib/GaPreviousScreenUtil';

const DISMISS_OUT_OF_HK_OVERLAY_AFTER = 10; // In second
const SYSTEM_MESSAGE_ICON_KEY = 'functional/alert';

export interface ChooseLocationContainerProps {
  selectedPolicy: string;
  type: string;
  locationTitle: string;
  locationErrorMsg: string;
  isGoogleMapJsScriptComplete: boolean;
  addressFromMapView: MapAddressInputType;
  confirmBtnLabel: string;
  addressOutOfHKSystemMessage: {
    title: LocalizedText;
    description: LocalizedText;
  };
  autoCompleteMapAddressReset: () => void;
  onloadGoogleAutoCompleteMap: (objLocation: any) => void;
  onAutoCompleteMapAddressChangedConfirm: () => void;
  setIsChosenNonHKAddress: (isNonHK: boolean) => void;
  setIsAutoFilled: (isAutoFilled: boolean) => void;
}

export interface ChooseLocationContainerState {
  showErrorCurrentPositionModal: boolean;
  isCurrentLocationError: boolean;
  isShowOutOfHKErrorMessage: boolean;
  isGettingCurrentLocation: boolean;
}

export class ChooseLocationContainer extends Component<
  ChooseLocationContainerProps,
  ChooseLocationContainerState
> {
  private outOfHKOverlayTimeout;
  constructor(props: any) {
    super(props);
    this.state = {
      showErrorCurrentPositionModal: false,
      isCurrentLocationError: false,
      isShowOutOfHKErrorMessage: false,
      isGettingCurrentLocation: false,
    };
  }

  componentDidMount() {
    const { selectedPolicy, autoCompleteMapAddressReset } = this.props;
    if (_.isEmpty(selectedPolicy)) {
      goto('emma-change-contact', undefined, undefined, true);
    } else {
      autoCompleteMapAddressReset();
      this.getLocation();
    }
  }

  componentDidUpdate(prevProps: ChooseLocationContainerProps) {
    const { addressFromMapView } = this.props;
    if (!_.isEqual(addressFromMapView, prevProps.addressFromMapView)) {
      this.handleAddressChanged();
    }
  }

  handleAddressChanged = () => {
    const { addressFromMapView } = this.props;
    if (
      addressFromMapView.region &&
      addressFromMapView.region !== 'Hong Kong'
    ) {
      this.showOrHideOutOfHKOverlay(true);
      this.outOfHKOverlayTimeout && clearTimeout(this.outOfHKOverlayTimeout);
      this.outOfHKOverlayTimeout = setTimeout(() => {
        this.showOrHideOutOfHKOverlay(false);
      }, DISMISS_OUT_OF_HK_OVERLAY_AFTER * 1000);
    }
  };

  showOrHideOutOfHKOverlay = (isShow: boolean) => {
    if (isApp() && isShow) {
      const { addressOutOfHKSystemMessage } = this.props;
      const title = resolveLocalizedText(addressOutOfHKSystemMessage.title);
      const description = resolveLocalizedText(
        addressOutOfHKSystemMessage.description,
      );
      EmmaFactory.createInstance().then((emma) => {
        emma.instance.showSystemMessageOverlay(
          title,
          description,
          SYSTEM_MESSAGE_ICON_KEY,
        );
      });
    } else {
      this.setState({ isShowOutOfHKErrorMessage: isShow });
    }
  };

  getLocation = async () => {
    let objLocation = {
      longitude: -1,
      latitude: -1,
    };
    this.setState({ isGettingCurrentLocation: true });
    if (isApp()) {
      try {
        const emma = await EmmaFactory.createInstance();
        const geolocation = await emma.instance.getGeolocation();
        if (geolocation.error) {
          console.error('Unable to get gelocation', geolocation.error);
          this.props.onloadGoogleAutoCompleteMap(undefined);
          if (
            geolocation.error.isPermissionDenied ||
            geolocation.error.isLocationUnavailable
          ) {
            this.handleLocationPermissionDenied();
          }
          return;
        }
        objLocation.longitude = geolocation.lng;
        objLocation.latitude = geolocation.lat;
        this.props.onloadGoogleAutoCompleteMap(objLocation);
      } catch (e) {
        console.error('Unable to get gelocation', e);
      } finally {
        this.setState({ isGettingCurrentLocation: false });
      }
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.setState({ isGettingCurrentLocation: false });
          objLocation = this.showPosition(pos);
          this.props.onloadGoogleAutoCompleteMap(objLocation);
        },
        (error) => {
          this.setState({ isGettingCurrentLocation: false });
          if (error.code === error.PERMISSION_DENIED) {
            this.handleLocationPermissionDenied();
          }
          this.props.onloadGoogleAutoCompleteMap(undefined);
        },
      );
    }
  };

  handleLocationPermissionDenied = () => {
    this.setState({
      isCurrentLocationError: true,
      showErrorCurrentPositionModal: true,
    });
    triggerPopupDisplayScreenChange('PermissionDenied');
  };

  showPosition(position: any) {
    const objLocation = {
      longitude: position.coords.longitude,
      latitude: position.coords.latitude,
    };
    return objLocation;
  }

  onConfirm = () => {
    const { addressFromMapView, setIsChosenNonHKAddress } = this.props;
    if (addressFromMapView.region === 'Hong Kong') {
      this.props.onAutoCompleteMapAddressChangedConfirm();
      this.props.setIsAutoFilled(true);
    } else {
      setIsChosenNonHKAddress(true);
    }
    browserHistory.goBack();
  };
  hideErrorCurrentPositionModal = () => {
    this.setState({
      showErrorCurrentPositionModal: false,
    });
    triggerPopupCloseScreenChange();
  };

  openAppSettings = () => {
    if (isApp()) {
      EmmaFactory.createInstance().then((emma) => {
        emma.instance.openAppSettings();
      });
    }
  };

  render() {
    const {
      showErrorCurrentPositionModal,
      isCurrentLocationError,
      isShowOutOfHKErrorMessage,
      isGettingCurrentLocation,
    } = this.state;
    const {
      confirmBtnLabel,
      isGoogleMapJsScriptComplete,
      addressFromMapView,
      addressOutOfHKSystemMessage,
    } = this.props;
    const { locationUnavailableDialog, noAddressDataAvailable } =
      ChangeAddressContent;

    const isShowNoDataMessage =
      isCurrentLocationError && !addressFromMapView.region;

    return (
      <div className="emma-choose-location-container">
        <div className="emma-stylesheet-grid-system">
          <EmmaNavTitle
            title={t('CUSTOMER_PROFILE_UPDATE_ADDRESS_MAP_VIEW_TITLE')}
            component={
              <div className="emma-choose-location-container__title">
                {t('CUSTOMER_PROFILE_UPDATE_ADDRESS_MAP_VIEW_TITLE')}
              </div>
            }
          />
        </div>
        <div className="emma-choose-location-container-google">
          {(isGettingCurrentLocation || !isGoogleMapJsScriptComplete) && (
            <div className="emma-choose-location-container-loadingView">
              <EmmaLoadingSpinner />
            </div>
          )}
          <div
            id="map-canvas"
            className="emma-choose-location-container-google-map"
          ></div>
          {isGoogleMapJsScriptComplete &&
            (Boolean(addressFromMapView.region) || isCurrentLocationError) && (
              <div className="emma-choose-location-container-google-currentLocationCard">
                <div
                  className={classNames(
                    EmmaText['Title_L/ComplexContent/Left/MineShaft'],
                  )}
                >
                  {isShowNoDataMessage
                    ? resolveLocalizedText(noAddressDataAvailable.title)
                    : addressFromMapView.streetBuildingEstate1}
                </div>
                <div
                  className={classNames(
                    EmmaText['BodyTextS/line-15em/Left/MineShaft'],
                    EmmaSpace.mt8,
                    EmmaSpace.mb16,
                  )}
                >
                  {isShowNoDataMessage
                    ? resolveLocalizedText(noAddressDataAvailable.content)
                    : addressFromMapView.streetBuildingEstate2}
                </div>
                <EmmaButton
                  onClick={this.onConfirm}
                  disabled={
                    isCurrentLocationError && !addressFromMapView.region
                  }
                  isFullWidth
                >
                  {confirmBtnLabel}
                </EmmaButton>
              </div>
            )}
          {!isApp() && isShowOutOfHKErrorMessage && (
            <EmmaSystemMessage
              containerClassName="emma-choose-location-container-systemMessage"
              iconKey={SYSTEM_MESSAGE_ICON_KEY}
              title={resolveLocalizedText(addressOutOfHKSystemMessage.title)}
              description={resolveLocalizedText(
                addressOutOfHKSystemMessage.description,
              )}
            />
          )}
        </div>
        {/* CICER23 */}
        <Dialog show={showErrorCurrentPositionModal}>
          <DialogHeader>
            <span>{resolveLocalizedText(locationUnavailableDialog.title)}</span>
          </DialogHeader>
          <DialogBody>
            <div className="prefill-address-modal-error-content">
              {resolveLocalizedText(locationUnavailableDialog.message)}
            </div>
          </DialogBody>
          <DialogFooter>
            <div className="d-flex flex-column">
              {isApp() && (
                <EmmaButton
                  className={classNames(EmmaSpace.mb8)}
                  onClick={() => {
                    this.openAppSettings();
                    this.hideErrorCurrentPositionModal();
                  }}
                >
                  {resolveLocalizedText(locationUnavailableDialog.settings)}
                </EmmaButton>
              )}
              <EmmaButton outline onClick={this.hideErrorCurrentPositionModal}>
                {resolveLocalizedText(locationUnavailableDialog.close)}
              </EmmaButton>
            </div>
          </DialogFooter>
        </Dialog>
      </div>
    );
  }

  static mapStateToProps(state: any, ownProps: any) {
    const selectedPolicy = getSelectedPolicy(state);
    const changeAddressLabels = getChangeAddressLabels(state);
    const { confirmBtnLabel } = changeAddressLabels;
    const isGoogleMapJsScriptComplete = getIsGoogleMapJsScriptComplete(state);
    const addressFromMapView = getAddressFromMapView(state);
    const addressOutOfHKSystemMessage = getAddressOutOfHKSystemMessage(state);
    return {
      selectedPolicy,
      type: ownProps.match.params.type,
      isGoogleMapJsScriptComplete,
      addressFromMapView,
      confirmBtnLabel: confirmBtnLabel
        ? resolveLocalizedText(confirmBtnLabel)
        : '',
      addressOutOfHKSystemMessage,
    };
  }

  static mapDispatchToProps(dispatch: any) {
    return {
      autoCompleteMapAddressReset: () => {
        dispatch(autoCompleteMapAddressReset());
      },
      onloadGoogleAutoCompleteMap: (objLocation: any) => {
        dispatch(loadGoogleAutoCompleteMap(objLocation));
      },
      onAutoCompleteMapAddressChangedConfirm: () => {
        dispatch(autoCompleteMapAddressChangedConfirm());
      },
      setIsChosenNonHKAddress: (isNonHK: boolean) => {
        dispatch(setIsChosenNonHKAddress(isNonHK));
      },
      setIsAutoFilled: (isAutoFilled: boolean) => {
        dispatch(setIsAutoFilled(isAutoFilled));
      },
    };
  }
}

export default connect(
  ChooseLocationContainer.mapStateToProps,
  ChooseLocationContainer.mapDispatchToProps,
)(ChooseLocationContainer);
